import React from 'react';
import { Box, Tooltip, IconButton, Chip, Typography } from '@mui/material';
import { ScatterPlot, ScatterPlotOutlined } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  activateNodeEditing,
  deactivateNodeEditing,
  selectIsEditingGeometry,
  selectEditingFeatureId,
} from '../store/slices/geometryEditingSlice';
import { selectSelectedFeatures } from '../store/slices/selectionSlice';

/**
 * NodeEditingToggle
 *
 * A toolbar button that toggles node editing mode for the currently selected
 * feature. When activated, the selected feature enters node editing mode where
 * individual vertices can be manipulated.
 *
 * **Validates: Requirement 16.1** – WHEN a Registered_User selects a Feature
 * and activates node editing mode, THE Geometry_Editor SHALL display all nodes
 * as editable points.
 *
 * Behaviour:
 * - Disabled when no feature is selected or when multiple features are selected
 * - Clicking toggles node editing on/off for the selected feature
 * - Shows a status chip when editing is active
 */
const NodeEditingToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const isEditingGeometry = useAppSelector(selectIsEditingGeometry);
  const editingFeatureId = useAppSelector(selectEditingFeatureId);
  const selectedFeatures = useAppSelector(selectSelectedFeatures);

  const hasSingleSelection = selectedFeatures.length === 1;
  const selectedFeature = hasSingleSelection ? selectedFeatures[0] : null;

  const canActivate = hasSingleSelection && !isEditingGeometry;
  const canDeactivate = isEditingGeometry;
  const isDisabled = !canActivate && !canDeactivate;

  const handleToggle = () => {
    if (isEditingGeometry) {
      dispatch(deactivateNodeEditing());
    } else if (selectedFeature) {
      dispatch(
        activateNodeEditing({
          featureId: selectedFeature.featureId,
          layerId: selectedFeature.layerId,
        })
      );
    }
  };

  const getTooltipText = (): string => {
    if (isEditingGeometry) {
      return 'Exit Node Editing';
    }
    if (!hasSingleSelection) {
      return 'Select a single feature to edit nodes';
    }
    return 'Edit Nodes';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={getTooltipText()} placement="bottom" arrow>
        <span>
          <IconButton
            size="small"
            onClick={handleToggle}
            disabled={isDisabled}
            aria-label={isEditingGeometry ? 'Exit node editing mode' : 'Enter node editing mode'}
            aria-pressed={isEditingGeometry}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '6px',
              color: isEditingGeometry ? '#4fc3f7' : '#c8c8c8',
              backgroundColor: isEditingGeometry ? 'rgba(79,195,247,0.15)' : 'transparent',
              border: isEditingGeometry
                ? '1px solid rgba(79,195,247,0.4)'
                : '1px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: '#fff',
              },
              '&.Mui-disabled': {
                color: '#555',
              },
            }}
          >
            {isEditingGeometry ? (
              <ScatterPlot sx={{ fontSize: 18 }} />
            ) : (
              <ScatterPlotOutlined sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Status indicator when editing */}
      {isEditingGeometry && editingFeatureId && (
        <Chip
          label={`Editing nodes: ${editingFeatureId}`}
          size="small"
          onDelete={handleToggle}
          sx={{
            backgroundColor: 'rgba(79,195,247,0.12)',
            color: '#4fc3f7',
            fontSize: '0.65rem',
            height: 20,
            maxWidth: 200,
            border: '1px solid rgba(79,195,247,0.3)',
            '& .MuiChip-deleteIcon': {
              color: '#4fc3f7',
              fontSize: 14,
              '&:hover': {
                color: '#fff',
              },
            },
          }}
        />
      )}

      {/* Hint when no feature is selected */}
      {!isEditingGeometry && selectedFeatures.length === 0 && (
        <Typography sx={{ color: '#555', fontSize: '0.65rem' }}>
          Select a feature to edit nodes
        </Typography>
      )}
    </Box>
  );
};

export default NodeEditingToggle;
